import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Pencil, Trash2, Car, Search } from 'lucide-react';
import { toast } from 'sonner';
import { CargaMasivaButton } from '../components/CargaMasivaButton';

export const VehiculosPage = () => {
  const [vehiculos, setVehiculos] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentVehiculo, setCurrentVehiculo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    patente: '',
    marca: '',
    modelo: '',
    año: '',
    capacidad: ''
  });

  // Filtrar vehículos basado en el término de búsqueda
  const filteredVehiculos = useMemo(() => {
    if (!searchTerm.trim()) return vehiculos;
    const term = searchTerm.toLowerCase();
    return vehiculos.filter(vehiculo => 
      vehiculo.patente?.toLowerCase().includes(term) ||
      vehiculo.marca?.toLowerCase().includes(term) ||
      vehiculo.modelo?.toLowerCase().includes(term) ||
      vehiculo.año?.toString().includes(term) ||
      vehiculo.capacidad?.toString().includes(term)
    );
  }, [vehiculos, searchTerm]);

  useEffect(() => {
    loadVehiculos();
  }, []);

  const loadVehiculos = async () => {
    try {
      const response = await api.get('/vehiculos');
      setVehiculos(response.data);
    } catch (error) {
      toast.error('Error al cargar vehículos');
    }
  };

  const handleOpenDialog = (vehiculo = null) => {
    if (vehiculo) {
      setCurrentVehiculo(vehiculo);
      setFormData({
        patente: vehiculo.patente,
        marca: vehiculo.marca,
        modelo: vehiculo.modelo,
        año: vehiculo.año || '',
        capacidad: vehiculo.capacidad || ''
      });
    } else {
      setCurrentVehiculo(null);
      setFormData({ patente: '', marca: '', modelo: '', año: '', capacidad: '' });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        año: formData.año ? parseInt(formData.año) : null,
        capacidad: formData.capacidad ? parseInt(formData.capacidad) : null
      };
      
      if (currentVehiculo) {
        await api.put(`/vehiculos/${currentVehiculo.id}`, data);
        toast.success('Vehículo actualizado');
      } else {
        await api.post('/vehiculos', data);
        toast.success('Vehículo creado');
      }
      await loadVehiculos();
      setShowDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar vehículo');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/vehiculos/${currentVehiculo.id}`);
      toast.success('Vehículo eliminado');
      await loadVehiculos();
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error('Error al eliminar vehículo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="vehiculos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-gray-900">Vehículos</h1>
          <p className="text-gray-600 mt-1">Gestión de flota de vehículos</p>
        </div>
        <div className="flex gap-2">
          <CargaMasivaButton
            endpoint="/vehiculos/carga-masiva"
            onSuccess={loadVehiculos}
            templateColumns={['patente', 'marca', 'modelo', 'año', 'capacidad']}
            templateName="vehiculos"
            entityName="Vehículos"
          />
          <Button onClick={() => handleOpenDialog()} data-testid="create-vehiculo-button">
            <Plus size={18} className="mr-2" />
            Nuevo Vehículo
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          {/* Buscador */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Buscar por patente, marca, modelo, año o capacidad..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-vehiculos-input"
              />
            </div>
            {searchTerm && (
              <p className="text-sm text-gray-500 mt-2">
                {filteredVehiculos.length} resultado{filteredVehiculos.length !== 1 ? 's' : ''} encontrado{filteredVehiculos.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Patente</TableHead>
                <TableHead>Marca</TableHead>
                <TableHead>Modelo</TableHead>
                <TableHead>Año</TableHead>
                <TableHead>Capacidad</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredVehiculos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                    {searchTerm ? 'No se encontraron vehículos con ese criterio' : 'No hay vehículos registrados'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredVehiculos.map((vehiculo) => (
                  <TableRow key={vehiculo.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Car size={16} className="text-primary" />
                        {vehiculo.patente}
                      </div>
                    </TableCell>
                    <TableCell>{vehiculo.marca}</TableCell>
                    <TableCell>{vehiculo.modelo}</TableCell>
                    <TableCell>{vehiculo.año || '-'}</TableCell>
                    <TableCell>{vehiculo.capacidad ? `${vehiculo.capacidad} pasajeros` : '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(vehiculo)}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setCurrentVehiculo(vehiculo);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="vehiculo-dialog">
          <DialogHeader>
            <DialogTitle>
              {currentVehiculo ? 'Editar Vehículo' : 'Nuevo Vehículo'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="patente">Patente *</Label>
              <Input
                id="patente"
                value={formData.patente}
                onChange={(e) => setFormData({ ...formData, patente: e.target.value.toUpperCase() })}
                required
                placeholder="AB1234"
                data-testid="patente-input"
              />
            </div>

            <div>
              <Label htmlFor="marca">Marca *</Label>
              <Input
                id="marca"
                value={formData.marca}
                onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                required
                data-testid="marca-input"
              />
            </div>

            <div>
              <Label htmlFor="modelo">Modelo *</Label>
              <Input
                id="modelo"
                value={formData.modelo}
                onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
                required
                data-testid="modelo-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="año">Año</Label>
                <Input
                  id="año"
                  type="number"
                  value={formData.año}
                  onChange={(e) => setFormData({ ...formData, año: e.target.value })}
                  placeholder="2024"
                  data-testid="año-input"
                />
              </div>

              <div>
                <Label htmlFor="capacidad">Capacidad</Label>
                <Input
                  id="capacidad"
                  type="number"
                  value={formData.capacidad}
                  onChange={(e) => setFormData({ ...formData, capacidad: e.target.value })}
                  placeholder="4"
                  data-testid="capacidad-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} data-testid="submit-vehiculo-button">
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Eliminación</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar el vehículo <strong>{currentVehiculo?.patente}</strong>?
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};